import { useState } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import classnames from "classnames";

import { Heading } from "common/core/typography";
import Button from "common/core/button";
import Icon from "common/core/icon";

import DocumentScrutinizer from "./scrutinizer";
import Styles from "./index.module.scss";

type PhotoId = {
  frontPicture: string | null;
  backPicture: string | null;
  documentClaimedType: string | null;
  selfiePicture: string | null;
  id: string;
};

type SecondaryId = {
  key: string | null;
  url: string | null;
} | null;

type TitleProps = {
  documentClaimedType: string | null;
};

type Props = {
  photos: ReturnType<typeof parsePhotosForDocumentViewer>;
  retake?: {
    initiate: () => void;
    inProgress: boolean;
  };
  className?: string;
};
type ActionProps = {
  onChangeDocument: (newDocumentIndex: number) => void;
  onChangeDocumentSide: (isFront: boolean) => void;
  currentPhotoIndex: number;
  isFront: boolean;
} & Props;

const MESSAGES = defineMessages({
  nextDocument: {
    id: "ae2ab2a2-ea56-46cf-968c-e7e2489eb0e1",
    defaultMessage: "Next document",
  },
  prevDocument: {
    id: "85dfc724-d88a-45cf-a146-6e8856baf60a",
    defaultMessage: "Previous document",
  },
});

export function parsePhotosForDocumentViewer({
  photoId,
  secondaryPhotoId,
  secondaryId,
}: {
  photoId: PhotoId | null;
  secondaryPhotoId: PhotoId | null;
  secondaryId: SecondaryId | null;
}) {
  return [
    ...(photoId?.frontPicture ? [photoId] : []),
    ...(secondaryPhotoId?.frontPicture
      ? [secondaryPhotoId]
      : secondaryId?.url
      ? [
          {
            frontPicture: secondaryId.url,
            backPicture: null,
            documentClaimedType: null,
            id: secondaryId.url,
          },
        ]
      : []),
    ...(photoId?.selfiePicture
      ? [
          {
            frontPicture: photoId.selfiePicture,
            backPicture: null,
            documentClaimedType: "selfie",
            id: photoId.selfiePicture,
          },
        ]
      : []),
  ];
}

function getPrimaryDocumentTitleFromClaimedType(documentClaimedType: string | null) {
  switch (documentClaimedType) {
    case "work_permit":
      return (
        <FormattedMessage id="ccf631e8-c131-4728-90aa-f9b6806fea61" defaultMessage="Work permit" />
      );
    case "voter_registration_card":
      return (
        <FormattedMessage
          id="66cd2fa0-0c6f-4aba-a911-3afb11a87456"
          defaultMessage="Voter registration card"
        />
      );
    case "property_insurance":
      return (
        <FormattedMessage
          id="eabd9b08-0e30-4938-ba79-8b6b14ec2dc3"
          defaultMessage="Property insurance"
        />
      );
    case "permanent_resident_card":
      return (
        <FormattedMessage
          id="0cbc9179-70a5-4675-ab9c-acd005abcbcf"
          defaultMessage="Permanent resident card"
        />
      );
    case "passport_card":
      return (
        <FormattedMessage
          id="c932de8e-d053-4ade-9799-f4beaeb1e6ea"
          defaultMessage="Passport card"
        />
      );
    case "passport":
      return (
        <FormattedMessage id="4fbc5738-16fc-4ac0-9d5e-26f2af1181bc" defaultMessage="Passport" />
      );
    case "driver_license":
      return (
        <FormattedMessage
          id="d0ceb473-a4c4-499b-a52d-1a58ad87f810"
          defaultMessage="Drivers license"
        />
      );
    case "certificate_of_citizenship":
      return (
        <FormattedMessage
          id="53c1cc89-2d45-4b36-8915-0ea0cd6062c1"
          defaultMessage="Certificate of citizenship"
        />
      );
    case "selfie":
      return <FormattedMessage id="53c1cc89-2d45-4b26-8915-0ea0cd6062c1" defaultMessage="Selfie" />;
    default:
      return (
        <FormattedMessage
          id="4b984546-6dd3-45c1-9a52-8d1cb3a56891"
          defaultMessage="Identity document"
        />
      );
  }
}

function Title(props: TitleProps) {
  const { documentClaimedType } = props;
  return (
    <Heading level="h2" textStyle="headingFive">
      {getPrimaryDocumentTitleFromClaimedType(documentClaimedType)}
    </Heading>
  );
}

function Actions(props: ActionProps) {
  const { photos, currentPhotoIndex, isFront, retake } = props;
  const intl = useIntl();
  const frontPicture = photos[currentPhotoIndex].frontPicture;
  const backPicture = photos[currentPhotoIndex].backPicture;
  const miniSquee = isFront ? backPicture : frontPicture;

  return (
    <div className={Styles.actions}>
      <div className={Styles.alternativeSideId}>
        {backPicture && (
          <Button
            buttonColor="dark"
            variant="tertiary"
            onClick={() => props.onChangeDocumentSide(!isFront)}
          >
            <img key={miniSquee} src={miniSquee!} alt="" />
            <FormattedMessage
              id="7b83bb72-6ad5-4378-963e-489bd7f7dcee"
              defaultMessage="View {isFront, select, true{back} other{front}}"
              values={{ isFront }}
            />
          </Button>
        )}
      </div>
      <div className={Styles.switchDocuments}>
        <Button
          variant="tertiary"
          buttonColor="action"
          onClick={() => {
            props.onChangeDocument(Math.max(currentPhotoIndex - 1, 0));
          }}
          disabled={currentPhotoIndex <= 0}
          aria-label={intl.formatMessage(MESSAGES.prevDocument)}
        >
          <Icon name="caret-left" />
        </Button>
        <FormattedMessage
          id="1f74bead-6564-4f35-b537-144dfe2d3711"
          defaultMessage="{currentPhotoIndex} of {totalDocumentCount}"
          values={{ currentPhotoIndex: currentPhotoIndex + 1, totalDocumentCount: photos.length }}
        />
        <Button
          variant="tertiary"
          buttonColor="action"
          onClick={() => {
            props.onChangeDocument(Math.min(currentPhotoIndex + 1, photos.length - 1));
          }}
          disabled={currentPhotoIndex >= photos.length - 1}
          aria-label={intl.formatMessage(MESSAGES.nextDocument)}
        >
          <Icon name="caret-right" />
        </Button>
      </div>
      <div>
        {Boolean(retake) && (
          <Button buttonColor="action" variant="primary" onClick={retake!.initiate}>
            <FormattedMessage
              id="2cd45bc5-3b8e-42ef-9929-6fc05f1d80c4"
              defaultMessage="Prompt mobile retake"
            />
          </Button>
        )}
      </div>
    </div>
  );
}

function IdentityDocumentViewer({ photos, retake, className }: Props) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFront, setIsFront] = useState(true);
  const documentUrl = isFront
    ? photos[currentPhotoIndex].frontPicture
    : photos[currentPhotoIndex].backPicture;

  if (!documentUrl) {
    throw new Error("Tried rendering IdentityDocumentViewer with no valid document URL");
  }

  return (
    <div className={classnames(Styles.main, className)}>
      <Title documentClaimedType={photos[currentPhotoIndex].documentClaimedType} />
      {/* here we want the scrutinizer to reset state with key when we change document */}
      <DocumentScrutinizer
        key={documentUrl}
        src={{
          type: documentUrl.endsWith(".pdf") ? "pdf" : "image",
          url: documentUrl,
        }}
        retake={retake}
      />
      <Actions
        onChangeDocument={(newDocumentIndex) => {
          setCurrentPhotoIndex(newDocumentIndex);
          setIsFront(true);
        }}
        onChangeDocumentSide={setIsFront}
        photos={photos}
        retake={retake}
        currentPhotoIndex={currentPhotoIndex}
        isFront={isFront}
      />
    </div>
  );
}

export default IdentityDocumentViewer;
